/**
 * eventMockData.ts
 *
 * Pre-populated mock events for UI mockup flows.
 */

export interface MockEventTicket {
  id?: number;
  ticket_type: string;
  price: number;
  quantity: number;
  description?: string;
  age_requirement?: string;
  sales_start_date?: string;
  sales_start_time?: string;
  sales_end_date?: string;
  sales_end_time?: string;
  has_end_purchase_period?: boolean;
  sub_events?: boolean;
  multi_day_access?: boolean;
}

export interface MockEventGuest {
  email: string;
}

export interface MockEventCollaborator {
  id: number;
  username: string;
  full_name: string;
  role?: string;
  prof_pics?: string;
}

export interface MockEventMedia {
  file_url: string;
}

export interface MockEvent {
  id: number;
  event_title: string;
  info: string;
  event_structure_type: 'single' | 'external';
  event_ticketing_type: 'internal' | 'external';
  external_ticketing_url?: string;
  start: string;
  end: string;
  timezone: string;
  location: string;
  venue: string;
  city: string;
  state: string;
  country: string;
  event_age?: string;
  genres: string[];
  medias: MockEventMedia[];
  tickets?: MockEventTicket[];
  guests?: MockEventGuest[];
  collaborators: MockEventCollaborator[];
  is_draft_or_published: 'draft' | 'published';
  is_cancelled: boolean;
  event_creator_id: number;
  tickets_sold?: number;
}

const CONCERT_IMG =
  'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=600&h=400&fit=crop';
const JAZZ_IMG =
  'https://images.unsplash.com/photo-1511192336575-5a79af67a629?w=600&h=400&fit=crop';
const ROCK_IMG =
  'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=400&fit=crop';
export const INITIAL_MOCK_EVENTS: MockEvent[] = [
  {
    id: 1,
    event_title: 'Music Safari 2026',
    info: 'A high-energy live music experience featuring DJs, performers, and guest artists across three stages.',
    event_structure_type: 'single',
    event_ticketing_type: 'internal',
    start: '2026-08-24T18:00:00',
    end: '2026-08-24T23:00:00',
    timezone: '(GMT-5) Eastern Time — New York',
    location: 'POINT (3.3792 6.5244)',
    venue: 'The Warehouse Arena',
    city: 'Lagos',
    state: 'Lagos',
    country: 'Nigeria',
    event_age: '18+',
    genres: ['Afrobeats', 'Hip-Hop'],
    medias: [{ file_url: CONCERT_IMG }],
    tickets: [
      {
        id: 1,
        ticket_type: 'General Admission',
        price: 25,
        quantity: 150,
        description: 'Standard entry',
        age_requirement: '18+',
        sales_start_date: '2026-07-01',
        sales_start_time: '10:00',
      },
      {
        id: 2,
        ticket_type: 'VIP',
        price: 75,
        quantity: 50,
        description: 'VIP access with backstage pass',
        age_requirement: '18+',
        sales_start_date: '2026-07-01',
        sales_start_time: '10:00',
      },
    ],
    guests: [
      { email: 'anabaker923@outlook.com' },
      { email: 'cynthiamorre@gmail.com' },
      { email: 'alex23@yahoo.com' },
    ],
    collaborators: [
      {
        id: 101,
        username: 'guyhawkins',
        full_name: 'Guy Hawkins',
        role: 'Organizer',
        prof_pics: '',
      },
      { id: 102, username: 'emmajohnson', full_name: 'Emma Johnson', role: 'Performer' },
    ],
    is_draft_or_published: 'published',
    is_cancelled: false,
    event_creator_id: 1,
    tickets_sold: 2684,
  },
  {
    id: 2,
    event_title: 'Midnight Jazz Fest 2027',
    info: 'An enchanting night filled with smooth jazz performances from renowned artists and emerging talents.',
    event_structure_type: 'single',
    event_ticketing_type: 'internal',
    start: '2027-02-14T20:00:00',
    end: '2027-02-15T02:00:00',
    timezone: '(GMT-6) Central Time — Chicago',
    location: 'POINT (-90.0715 29.9511)',
    venue: 'City Hall',
    city: 'New Orleans',
    state: 'Louisiana',
    country: 'USA',
    event_age: '21+',
    genres: ['Jazz', 'Soul'],
    medias: [{ file_url: JAZZ_IMG }],
    tickets: [
      {
        id: 3,
        ticket_type: 'Regular',
        price: 45,
        quantity: 200,
        age_requirement: '21+',
        sales_start_date: '2026-12-01',
        sales_start_time: '09:00',
      },
    ],
    collaborators: [{ id: 103, username: 'lucassmith', full_name: 'Lucas Smith', role: 'DJ' }],
    is_draft_or_published: 'published',
    is_cancelled: false,
    event_creator_id: 1,
    tickets_sold: 1200,
  },
  {
    id: 3,
    event_title: 'Symphony Under the Stars 2026',
    info: "An outdoor concert featuring the city's symphony orchestra, performing timeless classics under a starlit sky.",
    event_structure_type: 'external',
    event_ticketing_type: 'external',
    external_ticketing_url: 'https://tickets.example.com/symphony-2026',
    start: '2026-07-15T19:30:00',
    end: '2026-07-15T22:00:00',
    timezone: '(GMT-5) Eastern Time — New York',
    location: 'POINT (-73.9654 40.7829)',
    venue: 'Central Park',
    city: 'New York',
    state: 'New York',
    country: 'USA',
    event_age: 'All Ages',
    genres: ['Classical', 'Orchestra'],
    medias: [
      {
        file_url:
          'https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=600&h=400&fit=crop',
      },
    ],
    collaborators: [
      { id: 104, username: 'emilyjohnson', full_name: 'Emily Johnson', role: 'Host / MC' },
    ],
    is_draft_or_published: 'published',
    is_cancelled: false,
    event_creator_id: 1,
    tickets_sold: 890,
  },
  {
    id: 4,
    event_title: 'Rock Revolution 2026',
    info: 'A festival celebrating rock music with performances from legendary bands and new artists, along with food and art.',
    event_structure_type: 'single',
    event_ticketing_type: 'internal',
    start: '2026-09-10T12:00:00',
    end: '2026-09-10T22:00:00',
    timezone: '(GMT-8) Pacific Time — Los Angeles',
    location: 'POINT (-118.2437 34.0522)',
    venue: 'The Festival Grounds',
    city: 'Los Angeles',
    state: 'California',
    country: 'USA',
    event_age: '18+',
    genres: ['Rock', 'Alternative'],
    medias: [{ file_url: ROCK_IMG }],
    tickets: [
      {
        id: 4,
        ticket_type: 'Day Pass',
        price: 60,
        quantity: 300,
        age_requirement: '18+',
        sales_start_date: '2026-08-01',
        sales_start_time: '10:00',
      },
      {
        id: 5,
        ticket_type: 'Weekend Pass',
        price: 100,
        quantity: 150,
        age_requirement: '18+',
        sales_start_date: '2026-08-01',
        sales_start_time: '10:00',
      },
    ],
    collaborators: [
      { id: 105, username: 'michaellee', full_name: 'Michael Lee', role: 'Performer' },
    ],
    is_draft_or_published: 'published',
    is_cancelled: false,
    event_creator_id: 1,
    tickets_sold: 3400,
  },
  {
    id: 5,
    event_title: 'Rave Night 2027',
    info: 'A thrilling night featuring top electronic DJs, immersive visuals, and an unforgettable dance experience.',
    event_structure_type: 'single',
    event_ticketing_type: 'internal',
    start: '2027-03-30T21:00:00',
    end: '2027-03-31T04:00:00',
    timezone: '(GMT+1) Central European Time — Berlin',
    location: 'POINT (13.4050 52.5200)',
    venue: 'The Dome',
    city: 'Berlin',
    state: 'Berlin',
    country: 'Germany',
    event_age: '18+',
    genres: ['Electronic', 'Dance'],
    medias: [
      {
        file_url:
          'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=600&h=400&fit=crop',
      },
    ],
    tickets: [
      {
        id: 6,
        ticket_type: 'Early Bird',
        price: 30,
        quantity: 0,
        age_requirement: '18+',
        sales_start_date: '2027-02-01',
        sales_start_time: '10:00',
      },
      {
        id: 7,
        ticket_type: 'Regular',
        price: 50,
        quantity: 80,
        age_requirement: '18+',
        sales_start_date: '2027-02-01',
        sales_start_time: '10:00',
      },
    ],
    collaborators: [{ id: 106, username: 'avabrown', full_name: 'Ava Brown', role: 'DJ' }],
    is_draft_or_published: 'draft',
    is_cancelled: false,
    event_creator_id: 1,
    tickets_sold: 0,
  },
  {
    id: 6,
    event_title: 'Smooth Vibes 2026',
    info: 'An intimate evening filled with live jazz performances and soulful melodies from renowned artists.',
    event_structure_type: 'single',
    event_ticketing_type: 'internal',
    start: '2026-09-15T19:00:00',
    end: '2026-09-15T22:00:00',
    timezone: '(GMT-6) Central Time — Chicago',
    location: 'POINT (-90.0715 29.9511)',
    venue: 'The Jazz Lounge',
    city: 'New Orleans',
    state: 'Louisiana',
    country: 'USA',
    genres: ['Jazz Night', 'Soul'],
    medias: [{ file_url: JAZZ_IMG }],
    tickets: [
      {
        id: 8,
        ticket_type: 'General',
        price: 35,
        quantity: 100,
        age_requirement: '21+',
        sales_start_date: '2026-08-01',
        sales_start_time: '10:00',
      },
    ],
    collaborators: [{ id: 107, username: 'liamsmith', full_name: 'Liam Smith', role: 'Organizer' }],
    is_draft_or_published: 'published',
    is_cancelled: false,
    event_creator_id: 1,
    tickets_sold: 420,
  },
  {
    id: 7,
    event_title: 'Indie Fest 2026',
    info: 'A vibrant festival celebrating emerging indie rock bands with multiple stages and food trucks.',
    event_structure_type: 'single',
    event_ticketing_type: 'internal',
    start: '2026-10-10T14:00:00',
    end: '2026-10-10T22:00:00',
    timezone: '(GMT-5) Eastern Time — New York',
    location: 'POINT (-73.9654 40.7829)',
    venue: 'Central Park',
    city: 'New York',
    state: 'New York',
    country: 'USA',
    genres: ['Indie Rock', 'Alternative'],
    medias: [
      {
        file_url:
          'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=600&h=400&fit=crop',
      },
    ],
    tickets: [
      {
        id: 9,
        ticket_type: 'Regular',
        price: 40,
        quantity: 250,
        age_requirement: '18+',
        sales_start_date: '2026-09-01',
        sales_start_time: '10:00',
      },
    ],
    collaborators: [
      { id: 108, username: 'sophiajohnson', full_name: 'Sophia Johnson', role: 'Host / MC' },
    ],
    is_draft_or_published: 'published',
    is_cancelled: false,
    event_creator_id: 1,
    tickets_sold: 1800,
  },
  {
    id: 8,
    event_title: 'Summer Sound Clash 2026',
    info: 'An immersive summer music festival featuring live performances, DJ sets, food vendors, and nightlife experiences in the heart of Miami.',
    event_structure_type: 'single',
    event_ticketing_type: 'internal',
    start: '2026-07-18T17:00:00',
    end: '2026-07-19T02:00:00',
    timezone: '(GMT-4) Eastern Daylight Time - Miami',
    location: 'POINT (-80.1918 25.7617)',
    venue: 'Miami Beach Convention Center',
    city: 'Miami',
    state: 'Florida',
    country: 'USA',
    event_age: '18+',
    genres: ['Afrobeats', 'Hip-Hop', 'Dance', 'Electronic'],
    medias: [
      {
        file_url:
          'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=600&h=400&fit=crop',
      },
      {
        file_url:
          'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=400&fit=crop',
      },
      {
        file_url:
          'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=600&h=400&fit=crop',
      },
      {
        file_url:
          'https://images.unsplash.com/photo-1511192336575-5a79af67a629?w=600&h=400&fit=crop',
      },
    ],
    tickets: [
      {
        id: 10,
        ticket_type: 'Early Bird',
        price: 45,
        quantity: 0,
        age_requirement: '18+',
        sales_start_date: '2026-05-01',
        sales_start_time: '10:00',
      },
      {
        id: 11,
        ticket_type: 'General Admission',
        price: 65,
        quantity: 500,
        age_requirement: '18+',
        sales_start_date: '2026-05-01',
        sales_start_time: '10:00',
      },
      {
        id: 12,
        ticket_type: 'VIP',
        price: 150,
        quantity: 100,
        age_requirement: '18+',
        sales_start_date: '2026-05-01',
        sales_start_time: '10:00',
      },
    ],
    collaborators: [
      { id: 101, username: 'guyhawkins', full_name: 'Guy Hawkins', role: 'Organizer' },
      { id: 102, username: 'emmajohnson', full_name: 'Emma Johnson', role: 'Performer' },
      { id: 103, username: 'lucassmith', full_name: 'Lucas Smith', role: 'DJ' },
    ],
    is_draft_or_published: 'published',
    is_cancelled: false,
    event_creator_id: 1,
    tickets_sold: 2684,
  },
];
